// The position drawer: one line of a requisition, PO or RFQ, and what the same item was bought
// for before (price, supplier, PO, date).
import { useQuery } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { formatDate, formatOrderAmount, formatUsdFull, REGIONS } from "@po/shared";
import { api, type PositionDetail } from "../lib/api";
import { int, spaced } from "../lib/format";
import { CriticalChip, StageBadge } from "./badges";
import type { DrawerRef } from "./Drawer";

const Figure = ({ label, children }: { label: string; children: ReactNode }) => (
  <div>
    <div className="text-[11px] text-muted">{label}</div>
    <div className="text-[14px] font-semibold tnum">{children}</div>
  </div>
);

/** A read-only field drawn like Sertica's Position Details boxes: the label beside the value. */
export const Box = ({ label, children, wide, area }: { label: string; children?: ReactNode; wide?: boolean; area?: boolean }) => (
  <div className={`grid grid-cols-[120px_minmax(0,1fr)] items-start gap-2 ${wide ? "md:col-span-2 lg:col-span-3" : ""}`}>
    <div className="text-[11.5px] text-ink2 pt-1">{label}:</div>
    <div
      className={`rounded-input border border-line bg-hover px-2 py-1 text-[12.5px] text-ink break-words ${area ? "min-h-[52px] whitespace-pre-line" : "min-h-[26px]"}`}
    >
      {children === "" || children == null ? "" : children}
    </div>
  </div>
);

const price = (n: number | null | undefined, currency: string | null | undefined) =>
  n == null
    ? null
    : currency
      ? formatOrderAmount(n, currency)
      : new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(n);

export function PositionBody({
  recordType,
  recordNo,
  positionNo,
  onOpen,
}: {
  recordType: "requisition" | "po" | "rfq";
  recordNo: string;
  positionNo: number;
  onOpen: (r: DrawerRef) => void;
}) {
  const {
    data: p,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["record", "position", recordType, recordNo, positionNo],
    queryFn: () =>
      api<PositionDetail>(`/positions/${recordType}/${encodeURIComponent(recordNo)}/${positionNo}`),
  });
  if (isLoading) return <div className="p-4 text-muted">Loading…</div>;
  if (isError || !p)
    return <div className="p-4 text-muted">This position is not in the dashboard's store.</div>;

  return (
    <div>
      <div className="px-4 pt-3 pb-2 space-y-1.5">
        <div className="label">
          {{ po: "PO", rfq: "RFQ", requisition: "Requisition" }[recordType]}{" "}
          <button
            className="text-brand hover:underline tnum"
            onClick={() => onOpen({ type: recordType, no: recordNo })}
          >
            {recordNo}
          </button>{" "}
          · position {positionNo}
        </div>
        <h2 className="text-[18px] font-extrabold text-ink flex flex-wrap items-center gap-2">
          {p.name ?? "—"}
          {p.critical && <CriticalChip remark={p.criticalRemark} />}
        </h2>
        {"rejected" in p && p.rejected && <p className="text-[12px] text-muted">Rejected line</p>}
      </div>

      {"quotedQuantity" in p ? (
        <QuoteDetails p={p} positionNo={positionNo} onOpen={onOpen} />
      ) : (
        <PositionDetails p={p} recordType={recordType === "po" ? "po" : "requisition"} positionNo={positionNo} onOpen={onOpen} />
      )}

      <PurchaseHistory
        history={p.history}
        measureUnit={p.measureUnit}
        onOpen={onOpen}
        intro={
          p.matchedBy === "item"
            ? `Every PO line ordering item ${p.itemNo}, on any vessel. Cancelled POs are left out.`
            : p.matchedBy === "name"
              ? p.itemNo
                ? `No stored PO line is linked to item ${p.itemNo} yet (older lines are still being read for their item), so PO lines are matched by the same name. Cancelled POs are left out.`
                : "This line has no item no., so PO lines are matched by the same name. Cancelled POs are left out."
              : "This line has no item no. and no name to match."
        }
      />
    </div>
  );
}

/**
 * Every PO line that ordered the same thing (the position's or the item's), with last, lowest and
 * highest price, narrowed by supplier region and country. `intro` says how lines were matched.
 */
export function PurchaseHistory({
  history: all,
  measureUnit,
  intro,
  onOpen,
}: {
  history: PositionDetail["history"];
  measureUnit: string | null | undefined;
  intro: ReactNode;
  onOpen: (r: DrawerRef) => void;
}) {
  // The supplier region and country the purchase history is narrowed to; "" is every one.
  const [region, setRegion] = useState("");
  const [country, setCountry] = useState("");
  // A supplier whose address is not read yet has no country or region: "?" lists those, last.
  const countryOf = (h: { supplierCountry: string | null }) => h.supplierCountry || "?";
  const regionOf = (h: { supplierRegion: string | null }) => h.supplierRegion || "?";
  const present = new Set(all.map(regionOf));
  const regions = [...REGIONS.filter((r) => present.has(r)), ...(present.has("?") ? ["?"] : [])];
  const inRegion = region ? all.filter((h) => regionOf(h) === region) : all;
  // The countries of the chosen region only.
  const countries = [...new Set(inRegion.map(countryOf))].sort((a, b) =>
    a === "?" ? 1 : b === "?" ? -1 : a.localeCompare(b),
  );
  const history = country ? inRegion.filter((h) => countryOf(h) === country) : inRegion;
  // Prices of placed orders only: a PO still in approval may change.
  const placed = history.filter((h) => h.ordered && h.unitPriceUsd != null);
  const usdPrices = placed.map((h) => h.unitPriceUsd!);
  const unit = measureUnit ? ` / ${measureUnit}` : "";
  return (
    <section className="px-4 py-3 border-t border-line" aria-label="Purchase history">
      <h3 className="label mb-1">Purchase history</h3>
      <p className="text-[11.5px] text-muted mb-2">{intro}</p>
      {(regions.length > 1 || countries.length > 1) && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-3">
          {regions.length > 1 && (
            <label className="flex items-center gap-2 text-[12px] text-ink2">
              Supplier region
              <select
                className="input text-[12px]"
                value={region}
                onChange={(e) => {
                  setRegion(e.target.value);
                  setCountry("");
                }}
              >
                <option value="">All regions</option>
                {regions.map((r) => (
                  <option key={r} value={r}>
                    {r === "?" ? "Unknown" : r}
                  </option>
                ))}
              </select>
            </label>
          )}
          {countries.length > 1 && (
            <label className="flex items-center gap-2 text-[12px] text-ink2">
              Supplier country
              <select className="input text-[12px]" value={country} onChange={(e) => setCountry(e.target.value)}>
                <option value="">All countries</option>
                {countries.map((c) => (
                  <option key={c} value={c}>
                    {c === "?" ? "Unknown" : c}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
      )}
      {usdPrices.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
          <Figure label={`Last price (USD${unit})`}>{formatUsdFull(usdPrices[0]!)}</Figure>
          <Figure label="Lowest">{formatUsdFull(Math.min(...usdPrices))}</Figure>
          <Figure label="Highest">{formatUsdFull(Math.max(...usdPrices))}</Figure>
          <Figure label="Orders placed">{placed.length}</Figure>
        </div>
      )}
      {history.length === 0 ? (
        <p className="text-[13px] text-muted">No purchase orders found.</p>
      ) : (
        <div className="overflow-x-auto -mx-4">
          <table className="w-full text-[12px] tnum">
            <thead>
              <tr className="text-muted border-b border-line">
                <th className="text-left font-semibold py-1 pl-4 pr-2">Date</th>
                <th className="text-left font-semibold pr-2">PO no.</th>
                <th className="text-left font-semibold pr-2">Supplier</th>
                <th className="text-left font-semibold pr-2">Vessel</th>
                <th className="text-right font-semibold pr-2">Qty</th>
                <th className="text-right font-semibold pr-2">Unit price</th>
                <th className="text-right font-semibold pr-4">USD / unit</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h) => (
                <tr
                  key={`${h.poNo}-${h.positionNo}`}
                  className={`border-b border-line align-top ${h.current ? "bg-hover" : ""}`}
                  title={h.current ? "This line" : undefined}
                >
                  <td
                    className="py-1 pl-4 pr-2 whitespace-nowrap"
                    title={h.ordered ? "Order date" : "Created; not placed yet"}
                  >
                    {formatDate(h.date)}
                    {!h.ordered && <div className="text-[10.5px] text-muted">not placed</div>}
                  </td>
                  <td className="pr-2 whitespace-nowrap">
                    {/* A PO outside the user's units is listed for its price only (SPEC §12). */}
                    {h.canOpen ? (
                      <button className="text-brand hover:underline" onClick={() => onOpen({ type: "po", no: h.poNo })}>
                        {h.poNo}/{h.positionNo}
                      </button>
                    ) : (
                      <span className="tnum" title="Not on your units: listed for reference">
                        {h.poNo}/{h.positionNo}
                      </span>
                    )}
                    {h.stage && (
                      <div>
                        <StageBadge stage={h.stage} lens="po" plain />
                      </div>
                    )}
                  </td>
                  <td className="pr-2 min-w-[140px]">
                    {h.supplier ?? "—"}
                    {h.supplierCountry && (
                      <div className="text-[10.5px] text-muted">{[h.supplierCountry, h.supplierRegion].filter(Boolean).join(" · ")}</div>
                    )}
                  </td>
                  <td className="pr-2 whitespace-nowrap">{h.vessel ?? "—"}</td>
                  <td className="pr-2 text-right whitespace-nowrap">
                    {int(h.quantity)} {h.measureUnit ?? ""}
                  </td>
                  <td className="pr-2 text-right whitespace-nowrap">
                    {price(h.unitPrice, h.currency)}
                    {h.discount > 0 && (
                      <div className="text-[10.5px] text-muted">−{h.discount}%</div>
                    )}
                  </td>
                  <td className="pr-4 text-right whitespace-nowrap font-semibold">
                    {h.unitPriceUsd != null ? formatUsdFull(h.unitPriceUsd) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {all.length >= 200 && (
        <p className="text-[11.5px] text-muted mt-2">Showing the newest 200.</p>
      )}
    </section>
  );
}

type QuoteLine = Extract<PositionDetail, { quotedQuantity: unknown }>;
type OrderLine = Exclude<PositionDetail, QuoteLine>;

const qty = (n: unknown) => (n == null || n === "" ? null : int(Number(n)));
const text = (v: unknown) => (v == null || v === "" ? null : String(v));
const dateOf = (v: unknown) => (typeof v === "string" && v ? formatDate(v) : null);

/**
 * Sertica's Position Details, field for field (SPEC §9): the line, its stock and price, its
 * remarks and technical references, then the order and receipt. Fields Sertica leaves empty stay
 * empty, as there; a line not read whole yet says so.
 */
function PositionDetails({
  p,
  recordType,
  positionNo,
  onOpen,
}: {
  p: OrderLine;
  recordType: "requisition" | "po";
  positionNo: number;
  onOpen: (r: DrawerRef) => void;
}) {
  const d = (p.details ?? {}) as Record<string, string | number | boolean>;
  const isReq = "requestedQuantity" in p;
  const quantity = isReq ? (p.quantityToOrder ?? p.requestedQuantity) : p.orderedQuantity;
  const unitPrice = isReq ? (typeof d.price === "number" ? d.price : null) : p.unitPrice;
  const currency = isReq ? text(d.currencyNo) : p.currency;
  const subtotal = isReq ? (unitPrice != null && quantity != null ? unitPrice * quantity : null) : p.totalPrice;
  // A requisition line's order and receipt are on the PO lines it went onto; a PO line's, its own.
  const orders: {
    poNo: string;
    positionNo: number;
    supplier: string | null;
    deliveryDate: string | null;
    ordered: number;
    received: number;
    rejected: number;
    receivedDate: string | null;
    receivedByUserNo: string | null;
    receivedByUserName: string | null;
  }[] = isReq
    ? p.orders
    : [
        {
          poNo: p.recordNo,
          positionNo,
          supplier: p.supplier,
          deliveryDate: p.deliveryDate,
          ordered: p.orderedQuantity,
          received: p.receivedQuantity,
          rejected: p.rejectedQuantity,
          receivedDate: p.latestReceivedDate,
          receivedByUserNo: text(d.receivedByUserNo),
          receivedByUserName: text(d.receivedByUserName),
        },
      ];
  return (
    <section className="px-4 py-3 border-t border-line" aria-label="Position details">
      <h3 className="label mb-2">Position details</h3>
      {p.details == null && <p className="text-[11.5px] text-muted mb-2">This line's Sertica details are not read yet; they are being filled in. Refresh the {recordType === "po" ? "PO" : "requisition"} from Sertica to read them now.</p>}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1.5">
        <Box label="Position No.">{positionNo}</Box>
        <Box label="Position Workflow">{isReq ? spaced(p.workflow) : null}</Box>
        <Box label="Vessel">{[p.unitNo, p.vessel].filter(Boolean).join(" · ")}</Box>
        <Box label="Name" wide>
          {p.name}
        </Box>
        <Box label="Requested Quantity">{isReq ? qty(p.requestedQuantity) : null}</Box>
        <Box label="Quantity to Order">{isReq ? qty(p.quantityToOrder) : null}</Box>
        <Box label="Measure Unit">{p.measureUnit}</Box>
        <Box label="Max. Stock">{qty(d.maxStock)}</Box>
        <Box label="Min. Stock">{qty(d.minStock)}</Box>
        <Box label="Stock">{qty(d.stock)}</Box>
        <Box label="Item">{p.itemNo}</Box>
        <Box label="Price">
          {price(unitPrice, currency)}
          {!isReq && p.discount > 0 && <span className="text-muted"> −{p.discount}%</span>}
        </Box>
        <Box label="Subtotal">{price(subtotal, currency)}</Box>
        <Box label="Currency">{currency}</Box>
        <Box label={isReq ? "Technical account" : "Account"}>
          {isReq ? [p.technicalAccountNo, p.technicalAccountName].filter(Boolean).join(" · ") : [p.accountNo, p.accountName].filter(Boolean).join(" · ")}
        </Box>
        {!isReq && <Box label="Value (USD)">{formatUsdFull(p.usdValue)}</Box>}
        <Box label="Remarks" wide area>
          {text(d.remark)}
        </Box>
        <Box label="Internal Remark" wide area>
          {text(d.internalRemark)}
        </Box>
        {!isReq && d.supplierRemark != null && (
          <Box label="Supplier Remark" wide area>
            {text(d.supplierRemark)}
          </Box>
        )}
        <Box label="Sup. Part No.">{text(d.supplierItemNo)}</Box>
        <Box label="Standard No.">{text(d.standardNo)}</Box>
        <Box label="Manufacturer">{text(d.manufacture)}</Box>
        <Box label="Drawing No.">{text(d.drawingNo)}</Box>
        <Box label="Dimension">{text(d.dimensions ?? d.dimension)}</Box>
        <Box label="Drawing Pos. No.">{text(d.drawingPosNo)}</Box>
        <Box label="Type No.">{text(d.typeNo)}</Box>
        <Box label="Reference No.">{text(d.referenceNo)}</Box>
        <Box label="Classification">{text(d.classification)}</Box>
        <Box label="Rating">{text(d.equipmentRating)}</Box>
        <Box label="Equipment Name">{text(d.equipmentName)}</Box>
        <Box label="Job">{[d.jobNo, d.jobName].filter(Boolean).join(" · ")}</Box>
        <Box label="Reject remark" wide area>
          {[text(d.rejectReasonName), text(d.rejectRemark)].filter(Boolean).join(": ")}
        </Box>
      </div>

      <div className="mt-3 space-y-3">
        {!orders.length && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1.5">
            <Box label="Purchase Order" />
            <Box label="Delivery Date">{dateOf(d.requestedDeliveryDate) && `requested ${dateOf(d.requestedDeliveryDate)}`}</Box>
          </div>
        )}
        {orders.map((o) => (
          <div key={`${o.poNo}/${o.positionNo}`} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1.5">
            <Box label="Purchase Order">
              <button className="text-brand hover:underline tnum" onClick={() => onOpen({ type: "po", no: o.poNo })}>
                {o.poNo}
              </button>
              {isReq && <span className="text-muted"> / {o.positionNo}</span>}
            </Box>
            <Box label="Delivery Date">{dateOf(o.deliveryDate)}</Box>
            <Box label="Supplier">{o.supplier}</Box>
            <Box label="Ordered Quantity">{int(o.ordered)}</Box>
            <Box label="Received Qty.">{o.received ? int(o.received) : null}</Box>
            <Box label="Rejected Qty.">{o.rejected ? <span className="text-[var(--flag-rejected_on_receipt)]">{int(o.rejected)}</span> : null}</Box>
            <Box label="Received Date">{dateOf(o.receivedDate)}</Box>
            <Box label="Received by">
              {o.receivedByUserNo && (
                <span className="tnum" title={o.receivedByUserName ?? undefined}>
                  {o.receivedByUserNo}
                </span>
              )}
            </Box>
          </div>
        ))}
      </div>
    </section>
  );
}

const TREND: Record<string, string> = { Increasing: "Higher than the last order", Decreasing: "Lower than the last order", Unchanged: "Same as the last order" };

/** An RFQ's quoted line, as Sertica's quote grid holds it: what was asked, what the supplier quoted. */
function QuoteDetails({ p, positionNo, onOpen }: { p: QuoteLine; positionNo: number; onOpen: (r: DrawerRef) => void }) {
  const d = (p.details ?? {}) as Record<string, string | number | boolean>;
  return (
    <section className="px-4 py-3 border-t border-line" aria-label="Quote details">
      <h3 className="label mb-2">Quote details</h3>
      {p.details == null && <p className="text-[11.5px] text-muted mb-2">This line's other Sertica fields are not read yet. Refresh the RFQ from Sertica to read them.</p>}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-1.5">
        <Box label="Position No.">{positionNo}</Box>
        <Box label="Vessel">{[p.unitNo, p.vessel].filter(Boolean).join(" · ")}</Box>
        <Box label="Supplier">{[p.supplierNo, p.supplier].filter(Boolean).join(" · ")}</Box>
        <Box label="Name" wide>
          {p.name}
        </Box>
        <Box label="Item">{p.itemNo}</Box>
        <Box label="Sup. Part No.">{p.supplierItemNo}</Box>
        <Box label="Measure Unit">{p.measureUnit}</Box>
        <Box label="Position Workflow">{spaced(p.workflow)}</Box>
        <Box label="Requested Quantity">{qty(p.requestedQuantity)}</Box>
        <Box label="Quoted Quantity">{qty(p.quotedQuantity)}</Box>
        <Box label="Quantity to Order">{qty(p.quantityToOrder)}</Box>
        <Box label="Price">
          {price(p.price, p.currency)}
          {!!p.discount && <span className="text-muted"> −{p.discount}%</span>}
        </Box>
        <Box label="Total">{price(p.totalPrice, p.currency)}</Box>
        <Box label="Total (USD)">{p.totalUsd != null ? formatUsdFull(p.totalUsd) : null}</Box>
        <Box label="Price Trend">{p.priceTrend && (TREND[p.priceTrend] ?? p.priceTrend)}</Box>
        <Box label="Purchase Order">
          {p.onPo &&
            (p.pos.length
              ? p.pos.map((no, i) => (
                  <span key={no}>
                    {i > 0 && ", "}
                    <button className="text-brand hover:underline tnum" onClick={() => onOpen({ type: "po", no })}>
                      {no}
                    </button>
                  </span>
                ))
              : "On the supplier's PO")}
        </Box>
        <Box label="Remarks" wide area>
          {text(d.remark)}
        </Box>
        <Box label="Internal Remark" wide area>
          {text(d.internalRemark)}
        </Box>
        <Box label="Item Name">{text(d.itemName)}</Box>
        <Box label="Item Reference No.">{text(d.itemReferenceNo)}</Box>
        <Box label="Standard No.">{text(d.standardNo)}</Box>
        <Box label="Manufacturer">{text(d.manufacture)}</Box>
        <Box label="Drawing No.">{text(d.drawingNo)}</Box>
        <Box label="Dimension">{text(d.dimensions)}</Box>
        <Box label="Drawing Pos. No.">{text(d.drawingPosNo)}</Box>
        <Box label="Type No.">{text(d.typeNo)}</Box>
        <Box label="Reference No.">{[text(d.referenceNo), text(d.referencePosNo)].filter(Boolean).join(" / ")}</Box>
        <Box label="Classification">{text(d.classification)}</Box>
        <Box label="From Requisitions">{d.totalQuantityFromRequisitions != null && `${qty(d.totalQuantityFromRequisitions)} ${text(d.measuringUnitNoFromRequisition) ?? ""}`}</Box>
      </div>
    </section>
  );
}
